import React, { useEffect, ReactNode, MouseEvent, useMemo, useState, useCallback, FC } from 'react';
import {
  UiRefreshType,
  useGetClinicUserQuery,
  useSubscribeToUiRefreshSubscription,
  useClearUserInvitationMutation,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import CreatePasswordModal from 'shared/components/CreatePasswordModal';
import { IOption } from 'shared/components/Dropdown';
import Sidebar from './components/Sidebar';
import TopToolbar from './components/TopToolbar';
import SidePanel from 'shared/components/SidePanel';
import { useAuth } from 'shared/providers/AuthProvider';
import { usePopover } from 'shared/providers/PopoverWindowProvider';
import { PopoverName } from 'shared/enums/PopoverName';
import VideoCall from 'pages/VideoCall';
import useUpdateCurrentClinic from 'shared/hooks/useUpdateCurrentClinic';
import useInAppNotifications from 'shared/hooks/useInAppNotifications';
import { useHistory } from 'react-router-dom';
import StripeTerminalProvider from 'shared/providers/StripeTerminalProvider';
import { loadStripe } from '@stripe/stripe-js';
import { Elements as StripeElementsProvider } from '@stripe/react-stripe-js';
import UnsupportedBrowserPopover from 'shared/components/UnsupportedBrowserPopover';
import useAutoPageReload from 'shared/hooks/useAutoPageReload';
import { env } from 'env';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Box, Flex, useColorMode, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import usePersistedState from 'shared/hooks/usePersistedState';
import { FeatureFlagName, GraphQLFetchPolicies } from 'shared/enums';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { updateCurrentClinicState } from 'state/currentClinic/currentClinicSlice';
import ChannelOverlay from './components/ChannelOverlay';
import ExportOrDownloadChannelModal from 'pages/Conversations/components/Modals/ExportOrDownloadChannel';
import PhoneCallProvider from 'shared/providers/PhoneCallProvider';
import { AppointmentModal } from 'pages/Appointments/components/Modals/AppointmentModal';
import { useTracking } from 'shared/providers/TrackingProvider';
import { GeneralEvents } from 'shared/providers/TrackingProvider/EventNames';
import HasFeature from 'shared/components/HasFeature';
import usePendo from 'shared/hooks/usePendo';
import { TopToolbarProvider } from './components/TopToolbar/context/TopToolbarContext';
import useIntercom from 'shared/hooks/useIntercom';

interface IProps {
  children: ReactNode;
}

const stripe = loadStripe(env.REACT_APP_STRIPE_API_KEY || '');
const disableAutoReloadPopovers: string[] = [PopoverName.PhoneCall, PopoverName.VideoCall];

const DefaultAuthenticated = ({ children }: IProps): JSX.Element => {
  const { logout, isImpersonating } = useAuth();
  const history = useHistory();
  const { popover, closePopover } = usePopover();
  const { clinicUser, currentClinicId, currentClinic } = useClinicUser();
  const { trackEvent } = useTracking();
  const { updateCurrentClinic } = useUpdateCurrentClinic();
  const [suggestRefreshMessage, setSuggestRefreshMessage] = useState<string>();
  const [, setNewClinicId] = usePersistedState(PersistedStateKeys.NewClinic, { id: '' });
  const { toggleColorMode, colorMode } = useColorMode();
  const [isReaderModeEnabled] = usePersistedState(PersistedStateKeys.IsReaderModeEnabled, false);
  const {
    isOpen: isCreatePasswordModalOpen,
    onClose: closeCreatePasswordModal,
    onOpen: openCreatePasswordModal,
  } = useDisclosure();

  usePendo();
  useIntercom();

  useEffect(() => {
    if (isReaderModeEnabled && colorMode !== 'dark') {
      toggleColorMode();
    }

    if (!isReaderModeEnabled && colorMode === 'dark') {
      toggleColorMode();
    }
  }, [isReaderModeEnabled, colorMode, toggleColorMode]);

  const { isMobile } = useResolutionProvider();
  const refreshPage = useCallback((): void => {
    trackEvent(GeneralEvents.UiRefreshedFromNotification, { currentClinicId });
    window.location.reload();
  }, [trackEvent, currentClinicId]);

  useSubscribeToUiRefreshSubscription({
    onData: ({ data }) => {
      if (data.data?.uiRefresh) {
        const { refreshType, message } = data.data?.uiRefresh;

        if (refreshType === UiRefreshType.HardRefresh) {
          const randomMilliseconds = Math.floor(Math.random() * 240 * 1000) + 1;
          setTimeout(() => {
            refreshPage();
          }, randomMilliseconds);
        } else {
          if (message) {
            setSuggestRefreshMessage(message);
          }
        }
      }
    },
  });

  useInAppNotifications({ clinicIds: currentClinicId ? [currentClinicId] : [] });

  const isAutoReloadDisabled = useMemo(
    () => !!(popover?.name && disableAutoReloadPopovers.includes(popover.name)),
    [popover?.name],
  );
  useAutoPageReload({ history, isDisabled: isAutoReloadDisabled });

  const [clearUserInvitation] = useClearUserInvitationMutation();
  const dispatch = useAppDispatch();
  const impersonationClinicId = useAppSelector((state) => state.currentClinic.clinicId);
  const isChannelViewOverlayOpen = useAppSelector((state) => state.conversations.channelViewData.isOpen);

  useGetClinicUserQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    onCompleted: (data) => {
      const currentClinicId = data.clinicUser?.vetInfo?.currentClinic?.id;
      if (currentClinicId && !(isImpersonating && impersonationClinicId)) {
        dispatch(updateCurrentClinicState({ clinicId: currentClinicId }));
      }

      /**
       * Ensure the user's invitation is moved out of "pending" state once
       * they log in, since they may have reached the app from an origin
       * other than their invitation email.
       */
      if (!!data.clinicUser?.invitationCode) {
        clearUserInvitation({
          variables: { userId: data.clinicUser.id },
          optimisticResponse: { updateOneUser: { __typename: 'User', id: data.clinicUser.id, invitationCode: null } },
        });
      }
    },
  });

  useEffect(() => {
    if (clinicUser?.isNewPasswordRequired) {
      openCreatePasswordModal();
    }
  }, [clinicUser, openCreatePasswordModal]);

  const onClinicSelected = useCallback(
    async (e: MouseEvent, option: IOption): Promise<void> => {
      if (!JSON.parse(localStorage.getItem(PersistedStateKeys.HasUnsavedSettingsChanges) || 'false')) {
        await updateCurrentClinic(option.value);
        const [redirectTo] = history.location.pathname.match(/^\/(settings.*|[a-z]+)\/?/) || [];
        if (redirectTo) {
          history.push(redirectTo);
        }
      } else {
        setNewClinicId({ id: option.value });
      }
    },
    [history, setNewClinicId, updateCurrentClinic],
  );

  const impersonationBannerText = useMemo(() => {
    return `Logged in as: ${clinicUser?.displayName} (${clinicUser?.email}) at ${currentClinic?.name}`;
  }, [clinicUser, currentClinic]);

  return (
    <PhoneCallProvider>
      <StripeTerminalProvider>
        <StripeElementsProvider stripe={stripe}>
          <TopToolbarProvider>
            <Flex flex="0 0 auto" direction="column" background="background.default">
              {isImpersonating && clinicUser && (
                <Alert
                  status="warning"
                  hideIcon
                  hideCloseButton
                  title={impersonationBannerText}
                  titleProps={{
                    textAlign: 'center',
                  }}
                  buttonProps={{
                    buttonText: 'Log Out',
                    buttonPosition: 'topRight',
                    onClick: (): void => {
                      logout();
                    },
                  }}
                />
              )}
              <HasFeature name={FeatureFlagName.EnablePendo}>
                <Box id="pendo-cross-app-guide" />
              </HasFeature>

              {!!suggestRefreshMessage && clinicUser && (
                <Alert
                  status="warning"
                  hideCloseButton
                  title={suggestRefreshMessage}
                  titleProps={{
                    textAlign: 'center',
                  }}
                  buttonProps={{
                    buttonText: 'Refresh Page',
                    iconName: 'doubleArrowCircle',
                    buttonPosition: 'topRight',
                    onClick: refreshPage,
                  }}
                />
              )}
            </Flex>
            <Flex
              overflow="hidden"
              className="Layout__Container"
              flexDirection={isMobile ? 'column-reverse' : 'row'}
              h="100%"
            >
              <Sidebar onClinicSelected={onClinicSelected} />
              <Flex className="Layout__Workspace" background="background.subtle" overflow="hidden" flex="1 1 auto">
                <Flex overflow="hidden" flex="1 1 auto" flexDirection="column">
                  <TopToolbar />
                  {children}
                </Flex>

                {isChannelViewOverlayOpen && <ChannelOverlay />}

                <SidePanel />

                <AppointmentModal />
                {isCreatePasswordModalOpen && <CreatePasswordModal onClose={closeCreatePasswordModal} />}

                {popover?.name === PopoverName.VideoCall && (
                  <VideoCall
                    channelId={popover?.data?.channelId}
                    startTop={popover?.startTop}
                    startLeft={popover?.startLeft}
                    clinicPetParentId={popover?.data?.clinicPetParentId}
                  />
                )}

                {popover?.name === PopoverName.UnsupportedBrowser && (
                  <UnsupportedBrowserPopover
                    startTop={popover?.startTop}
                    startLeft={popover?.startLeft}
                    closePopover={closePopover}
                  />
                )}

                <ExportOrDownloadChannelModal />
              </Flex>
            </Flex>
          </TopToolbarProvider>
        </StripeElementsProvider>
      </StripeTerminalProvider>
    </PhoneCallProvider>
  );
};

export default DefaultAuthenticated;

export const ContentLayout: FC = ({ children }) => (
  <Box flex="1 1 auto" overflow="auto" width="100%" className="DefaultAuthenticated__ContentLayout">
    {children}
  </Box>
);
